import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  Check,
  Sparkles,
} from 'lucide-react';

import { CardImage } from '@/components/CardImage';
import { CardDetailSheet } from '@/components/CardDetailSheet';
import { useCards, type Card } from '@/hooks/useCards';
import { useCollection } from '@/hooks/useCollection';
import { useAuth } from '@/hooks/useAuth';
import type { InkColor } from '@/lib/ink-utils';

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

type BinderType =
  | 'regular'
  | 'grand-master'
  | 'master'
  | 'character'
  | 'custom';

type Pocket = 4 | 9 | 12;

interface Binder {
  id: string;
  name: string;
  type: BinderType;
  pocket: Pocket;
  setName?: string;      // for master / grand-master
  character?: string;    // for character binder (matches classifications)
  cardIds: string[];     // for regular + custom template (ordered)
}

const STORAGE_KEY = 'lorescout:binders:v1';

/* ------------------------------------------------------------------ */
/* Storage                                                            */
/* ------------------------------------------------------------------ */

function loadBinders(): Binder[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveBinders(binders: Binder[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(binders));
  } catch {
    /* ignore quota */
  }
}

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const TYPE_LABEL: Record<BinderType, string> = {
  regular: 'Regular',
  'grand-master': 'Grand Master Set',
  master: 'Master Set',
  character: 'Character Binder',
  custom: 'Custom Template',
};

const TYPE_HINT: Record<BinderType, string> = {
  regular: 'Add cards in any order — how you organize your real binder.',
  'grand-master': 'Every printing of a set, including Enchanteds.',
  master: 'One of each card in a set (no Enchanteds).',
  character: 'Every card featuring a chosen character.',
  custom: 'A template of cards you\'re working towards. Missing ones grey out.',
};

function sortBySetNumber(cards: Card[]): Card[] {
  return [...cards].sort((a, b) => {
    const na = parseInt(a.set_number || '', 10);
    const nb = parseInt(b.set_number || '', 10);
    const aNum = isNaN(na) ? Infinity : na;
    const bNum = isNaN(nb) ? Infinity : nb;
    if (aNum !== bNum) return aNum - bNum;
    return (a.set_number || '').localeCompare(b.set_number || '');
  });
}

/** Resolve the ordered list of cards for a binder based on its type. */
function resolveBinderCards(binder: Binder, allCards: Card[]): Card[] {
  const byId = new Map(allCards.map((c) => [c.id, c]));

  switch (binder.type) {
    case 'grand-master': {
      if (!binder.setName) return [];
      return sortBySetNumber(
        allCards.filter((c) => c.set_name === binder.setName),
      );
    }
    case 'master': {
      if (!binder.setName) return [];
      return sortBySetNumber(
        allCards.filter((c) => c.set_name === binder.setName && !c.enchanted),
      );
    }
    case 'character': {
      if (!binder.character) return [];
      const needle = binder.character.toLowerCase();
      return allCards
        .filter((c) => {
          if (c.name.toLowerCase().includes(needle)) return true;
          const cls = (c.classifications || []).map((x) => x.toLowerCase());
          return cls.some((x) => x.includes(needle));
        })
        .sort((a, b) =>
          (a.set_name + a.set_number).localeCompare(b.set_name + b.set_number),
        );
    }
    case 'regular':
    case 'custom':
    default: {
      return binder.cardIds
        .map((id) => byId.get(id))
        .filter(Boolean) as Card[];
    }
  }
}

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */

const ACTIVE_KEY = 'lorescout:binder:active:v1';
const pageKey = (id: string) => `lorescout:binder:page:${id}:v1`;

// Module-scoped image cache with TTL eviction so long browsing sessions
// don't accumulate memory. Entries expire after IMAGE_TTL_MS and the
// cache is capped at IMAGE_CACHE_MAX; a lightweight LRU-ish sweep runs
// on every insert.
const IMAGE_TTL_MS = 5 * 60 * 1000;   // 5 minutes
const IMAGE_CACHE_MAX = 240;          // ~20 binder pages of images

interface CachedImage { img: HTMLImageElement; ts: number }
const imageCache = new Map<string, CachedImage>();

function cacheImage(src: string): HTMLImageElement {
  const now = Date.now();
  const existing = imageCache.get(src);
  if (existing) {
    existing.ts = now;
    // Refresh LRU position by re-inserting.
    imageCache.delete(src);
    imageCache.set(src, existing);
    return existing.img;
  }
  // Evict expired entries.
  for (const [k, v] of imageCache) {
    if (now - v.ts > IMAGE_TTL_MS) imageCache.delete(k);
  }
  // Evict oldest until under cap.
  while (imageCache.size >= IMAGE_CACHE_MAX) {
    const oldestKey = imageCache.keys().next().value as string | undefined;
    if (!oldestKey) break;
    imageCache.delete(oldestKey);
  }
  const img = new Image();
  img.decoding = 'async';
  img.src = src;
  imageCache.set(src, { img, ts: now });
  return img;
}

export function BinderView() {
  const { user } = useAuth();
  const { data: allCards, isLoading } = useCards({});
  const { data: collection } = useCollection();

  const [binders, setBinders] = useState<Binder[]>(() => loadBinders());
  const [activeId, setActiveId] = useState<string | null>(() => {
    try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; }
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingBinder, setEditingBinder] = useState<Binder | null>(null);
  const [pageIdx, setPageIdx] = useState(0);

  // Persist binders to localStorage whenever they change.
  useEffect(() => { saveBinders(binders); }, [binders]);

  // Ensure an active binder is always selected once we have some.
  useEffect(() => {
    if (!activeId && binders.length > 0) setActiveId(binders[0].id);
    if (activeId && !binders.find((b) => b.id === activeId)) {
      setActiveId(binders[0]?.id ?? null);
    }
  }, [binders, activeId]);

  // Persist active binder id
  useEffect(() => {
    try {
      if (activeId) localStorage.setItem(ACTIVE_KEY, activeId);
      else localStorage.removeItem(ACTIVE_KEY);
    } catch { /* ignore quota */ }
  }, [activeId]);

  // Build per-card lookup for owned collection items (for scanned photos).
  const ownedByCardId = useMemo(() => {
    const m = new Map<string, { photo_url: string | null }>();
    for (const item of collection || []) {
      if (!m.has(item.card_id)) m.set(item.card_id, { photo_url: item.photo_url });
    }
    return m;
  }, [collection]);
  const ownedIds = useMemo(() => new Set(ownedByCardId.keys()), [ownedByCardId]);

  const active = binders.find((b) => b.id === activeId) || null;

  const sets = useMemo(() => {
    if (!allCards) return [];
    return Array.from(new Set(allCards.map((c) => c.set_name))).sort();
  }, [allCards]);

  const binderCards = useMemo(
    () => (active && allCards ? resolveBinderCards(active, allCards) : []),
    [active, allCards],
  );

  const pocket = active?.pocket ?? 9;
  const totalPages = Math.max(1, Math.ceil(binderCards.length / pocket));
  const safePage = Math.min(pageIdx, totalPages - 1);
  const pageCards = binderCards.slice(
    safePage * pocket,
    safePage * pocket + pocket,
  );
  const ownedInBinder = binderCards.filter((c) => ownedIds.has(c.id)).length;

  // Cover-opening animation state — user taps the closed cover to open.
  const [coverOpen, setCoverOpen] = useState(false);
  const [coverOpening, setCoverOpening] = useState(false);

  const openCover = () => {
    if (coverOpen || coverOpening) return;
    setCoverOpening(true);
    // Match the CSS animation duration (1.1s) so we swap once the cover is out of view.
    window.setTimeout(() => {
      setCoverOpen(true);
      setCoverOpening(false);
    }, 1050);
  };

  // When the active binder changes, restore saved page + reset the cover.
  useEffect(() => {
    setCoverOpen(false);
    setCoverOpening(false);
    if (!activeId) { setPageIdx(0); return; }
    let saved = 0;
    try {
      const raw = localStorage.getItem(pageKey(activeId));
      saved = raw ? Math.max(0, parseInt(raw, 10) || 0) : 0;

    } catch { /* ignore */ }
    setPageIdx(saved);
  }, [activeId]);

  // Persist current page whenever it changes for the active binder.
  useEffect(() => {
    if (!activeId) return;
    try { localStorage.setItem(pageKey(activeId), String(safePage)); } catch { /* ignore */ }
  }, [activeId, safePage]);

  // Preload images for the next ±2 pages so page-flips + swipes feel instant.
  // Uses an in-memory cache so we don't re-request the same image on every render.
  useEffect(() => {
    if (!active) return;
    const neighborStarts = [safePage - 2, safePage - 1, safePage + 1, safePage + 2]
      .filter((p) => p >= 0 && p < totalPages);
    for (const p of neighborStarts) {
      const cards = binderCards.slice(p * pocket, p * pocket + pocket);
      for (const c of cards) {
        const owned = ownedByCardId.get(c.id);
        const src = owned?.photo_url || c.image_url;
        if (!src) continue;
        cacheImage(src);
      }
    }
  }, [active, safePage, totalPages, binderCards, pocket, ownedByCardId]);

  // Page-flip animation state
  const [flipDir, setFlipDir] = useState<null | 'forward' | 'back'>(null);
  const [flipCards, setFlipCards] = useState<Card[]>([]);
  const goToPage = (next: number) => {
    const clamped = Math.max(0, Math.min(totalPages - 1, next));
    if (clamped === safePage || flipDir) return;
    const dir: 'forward' | 'back' = clamped > safePage ? 'forward' : 'back';
    // Snapshot the outgoing page so the flip overlay shows the current view.
    setFlipCards(pageCards);
    setFlipDir(dir);
    setPageIdx(clamped);
    setTimeout(() => setFlipDir(null), 600);

  };


  const openCreate = () => {
    setEditingBinder({
      id: uid(),
      name: 'New Binder',
      type: 'regular',
      pocket: 9,
      cardIds: [],
    });
    setEditorOpen(true);
  };

  const openEdit = () => {
    if (!active) return;
    setEditingBinder({ ...active });
    setEditorOpen(true);
  };

  const saveEditor = (b: Binder) => {
    setBinders((prev) => {
      const found = prev.find((x) => x.id === b.id);
      if (found) return prev.map((x) => (x.id === b.id ? b : x));
      return [...prev, b];
    });
    setActiveId(b.id);
    setEditorOpen(false);
  };

  const deleteActive = () => {
    if (!active) return;
    if (!confirm(`Delete binder "${active.name}"?`)) return;
    setBinders((prev) => prev.filter((b) => b.id !== active.id));
  };

  /* --------------------------- swipe -------------------------------- */
  const pageRef = useRef<HTMLDivElement | null>(null);
  const touchX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 40) return;
    goToPage(safePage + (dx < 0 ? 1 : -1));
  };

  const gridColsClass =
    pocket === 4 ? 'grid-cols-2' : pocket === 12 ? 'grid-cols-4' : 'grid-cols-3';

  const [hoverCard, setHoverCard] = useState<Card | null>(null);
  const [detailCard, setDetailCard] = useState<Card | null>(null);

  const renderPocketGrid = (cards: Card[], pageStart: number) => (
    <div className={`grid gap-2 ${gridColsClass}`}>
      {Array.from({ length: pocket }).map((_, i) => {
        const card = cards[i];
        const pocketNumber = pageStart + i + 1;
        if (!card) {
          return (
            <div
              key={i}
              className="relative aspect-[2/3] rounded-lg border border-dashed border-border/50 bg-background/40"
            >
              <span className="absolute top-1 left-1 rounded bg-background/70 px-1 py-0.5 text-[8px] font-mono text-muted-foreground">
                {pocketNumber}
              </span>
            </div>
          );
        }
        const ownedItem = ownedByCardId.get(card.id);
        const owned = !!ownedItem;
        const imgSrc = ownedItem?.photo_url || card.image_url;
        return (
          <div
            key={card.id}
            className="relative group cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              // Click/tap only — hover-based open/close created a flicker loop
              // when the preview overlay covered the source card (mouseleave
              // → close → mouseenter → open …). Click is unambiguous.
              setHoverCard((h) => (h?.id === card.id ? null : card));
            }}
          >
            <CardImage
              src={imgSrc}
              alt={card.name}
              inkColor={card.ink_color as InkColor}
              enchanted={card.enchanted || card.rarity === 'Enchanted'}
              shiny={
                card.enchanted ||
                ['Enchanted', 'Iconic', 'Epic', 'Special', 'Super Rare', 'Legendary', 'Rare'].includes(card.rarity || '')
              }
              holo={card.enchanted || ['Enchanted', 'Iconic', 'Epic'].includes(card.rarity || '')}
              showGlow={owned}
              className={`aspect-[2/3] transition-all ${
                owned ? '' : 'grayscale opacity-30'
              } group-hover:ring-2 group-hover:ring-primary/70`}
            />
            <span className="absolute top-1 left-1 rounded bg-background/80 px-1 py-0.5 text-[8px] font-mono text-foreground">
              {pocketNumber}
            </span>
            {card.set_number && (
              <span className="absolute bottom-1 right-1 rounded bg-background/80 px-1 py-0.5 text-[8px] font-mono text-foreground">
                #{card.set_number}
              </span>
            )}
            {!owned && (
              <span className="absolute bottom-1 left-1 rounded bg-background/80 px-1 py-0.5 text-[8px] font-body text-muted-foreground">
                Missing
              </span>
            )}
          </div>
        );
      })}
    </div>
  );



  /* --------------------------- render ------------------------------- */

  return (
    <div className="mt-3 space-y-3">
      {/* Header */}
      <div className="rounded-xl border border-border bg-card p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            <h3 className="font-display text-sm font-semibold text-foreground">
              Binder Mode
            </h3>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[10px] font-medium font-body text-primary-foreground"
          >
            <Plus className="h-3 w-3" /> New Binder
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground font-body">
          Virtual binder. Swipe or tap to turn pages. Owned cards appear in
          full color; cards you don't own yet are greyed out.
        </p>

        {/* Binder chips */}
        {binders.length > 0 && (
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">
              My Binders
            </p>
            <div className="flex flex-wrap gap-1.5">
              {binders.map((b) => (
                <button
                  key={b.id}
                  onClick={() => setActiveId(b.id)}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${
                    activeId === b.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-secondary-foreground'
                  }`}
                >
                  {b.name}
                  <span className="ml-1 opacity-60">· {b.pocket}p</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {active && (
          <>
            <div className="flex items-center justify-between text-[10px] text-muted-foreground font-body">
              <span>
                <span className="uppercase tracking-wider">Type</span>
                <span className="ml-1 text-foreground">
                  {TYPE_LABEL[active.type]}
                </span>
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={openEdit}
                  aria-label="Edit binder"
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={deleteActive}
                  aria-label="Delete binder"
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {binderCards.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] text-muted-foreground font-body">
                    {ownedInBinder}/{binderCards.length} owned
                  </span>
                  <span className="text-[10px] text-primary font-body">
                    {Math.round((ownedInBinder / binderCards.length) * 100)}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{
                      width: `${(ownedInBinder / binderCards.length) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Empty state */}
      {!active && (
        <div className="rounded-xl border border-dashed border-border bg-card/40 p-8 text-center">
          <p className="text-4xl mb-3">📖</p>
          <p className="text-sm text-foreground font-body mb-1">
            No binders yet
          </p>
          <p className="text-[11px] text-muted-foreground font-body mb-4">
            Build a virtual binder to track your collection or plan a chase.
          </p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-xs font-medium font-body text-primary-foreground"
          >
            <Plus className="h-3.5 w-3.5" /> Create your first binder
          </button>
        </div>
      )}

      {/* Binder page */}
      {active && (
        <>
          {isLoading ? (
            <div className="py-12 text-center text-xs text-muted-foreground font-body">
              Loading binder…
            </div>
          ) : binderCards.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card/40 p-8 text-center">
              <p className="text-sm text-muted-foreground font-body mb-3">
                This binder is empty.
              </p>
              <button
                onClick={openEdit}
                className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-[11px] font-medium font-body text-secondary-foreground"
              >
                <Pencil className="h-3 w-3" /> Configure binder
              </button>
            </div>
          ) : (
            <>
              <div className="binder-scene relative px-10">
                {/* Outside chevron arrows */}
                {coverOpen && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (safePage === 0) {
                          // Close the binder back to the cover
                          setCoverOpen(false);
                          setCoverOpening(false);
                        } else {
                          goToPage(safePage - 1);
                        }
                      }}
                      disabled={!!flipDir}
                      aria-label={safePage === 0 ? 'Back to cover' : 'Previous page'}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-50 flex h-12 w-8 items-center justify-center text-primary/90 hover:text-primary disabled:opacity-20 text-3xl font-thin font-display leading-none select-none"
                    >
                      &lt;
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); goToPage(safePage + 1); }}
                      disabled={safePage >= totalPages - 1 || !!flipDir}
                      aria-label="Next page"
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-50 flex h-12 w-8 items-center justify-center text-primary/90 hover:text-primary disabled:opacity-20 text-3xl font-thin font-display leading-none select-none"
                    >
                      &gt;
                    </button>
                  </>
                )}

                {/* Closed cover — looks like a real binder; tap to open */}
                {!coverOpen && (
                  <button
                    type="button"
                    onClick={openCover}
                    aria-label={`Open ${active.name}`}
                    className={`binder-cover ${coverOpening ? 'binder-cover-opening' : ''} absolute inset-x-10 inset-y-0 z-30 rounded-r-xl rounded-l-md overflow-hidden border-2 border-primary/60 flex flex-col items-center justify-center text-center px-6 cursor-pointer shadow-2xl`}
                    style={{
                      background:
                        'linear-gradient(135deg, hsl(230 40% 14%) 0%, hsl(230 35% 9%) 45%, hsl(230 45% 7%) 100%)',
                    }}
                  >
                    {/* Binder spine */}
                    <div className="absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-black/70 to-transparent" />
                    {/* Binder rings */}
                    <div className="absolute left-2 inset-y-6 flex flex-col justify-around">
                      {[0,1,2].map(i => (
                        <span key={i} className="block h-4 w-4 rounded-full border-2 border-primary/70 bg-background/60 shadow-inner" />
                      ))}
                    </div>
                    {/* Debossed label frame */}
                    <div className="rounded-md border border-primary/40 px-5 py-4 bg-background/20 backdrop-blur-sm">
                      <BookOpen className="h-8 w-8 text-primary mx-auto mb-2" />
                      <p className="font-display text-lg font-semibold text-foreground">
                        {active.name}
                      </p>
                      <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-primary/80 font-body">
                        {TYPE_LABEL[active.type]} · {active.pocket}-pocket
                      </p>
                    </div>
                    <p className="mt-4 text-[10px] text-muted-foreground font-body">
                      {coverOpening ? 'Opening…' : 'Tap to open'}
                    </p>
                  </button>
                )}

                <div
                  ref={pageRef}
                  onTouchStart={onTouchStart}
                  onTouchEnd={onTouchEnd}
                  className={`relative rounded-xl border-2 border-border bg-gradient-to-br from-card to-secondary p-3 shadow-inner select-none ${
                    coverOpen ? 'binder-page-in' : ''
                  }`}
                >
                  {renderPocketGrid(pageCards, safePage * pocket)}

                  {/* Flip overlay showing the outgoing page mid-turn */}
                  {flipDir && (
                    <div
                      className={`absolute inset-0 z-20 rounded-xl border-2 border-border bg-gradient-to-br from-card to-secondary p-3 ${
                        flipDir === 'forward'
                          ? 'page-flip-forward'
                          : 'page-flip-back'
                      }`}
                      aria-hidden
                    >
                      {renderPocketGrid(flipCards, safePage * pocket)}
                    </div>
                  )}
                </div>

                {/* Hover / tap preview — anchored inside the binder scene so the
                    outside chevrons (z-50) are never covered. */}
                {hoverCard && (
                  <div
                    className="absolute inset-x-10 inset-y-0 z-40 flex items-center justify-center p-2 pointer-events-none"
                    aria-modal="true"
                    role="dialog"
                  >
                    {/* Dimmer only over the page area, not the chevrons */}
                    <div
                      className="absolute inset-0 bg-background/70 backdrop-blur-sm rounded-xl pointer-events-auto animate-fade-in"
                      onClick={() => setHoverCard(null)}
                    />
                    <div
                      className="relative w-[70%] max-w-[240px] pointer-events-auto animate-scale-in"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => setDetailCard(hoverCard)}
                        className="block w-full text-left focus:outline-none focus:ring-2 focus:ring-primary rounded-lg"
                        aria-label={`Open full details for ${hoverCard.name}`}
                      >
                        <CardImage
                          src={ownedByCardId.get(hoverCard.id)?.photo_url || hoverCard.image_url}
                          alt={hoverCard.name}
                          inkColor={hoverCard.ink_color as InkColor}
                          enchanted={hoverCard.enchanted || hoverCard.rarity === 'Enchanted'}
                          shiny={
                            hoverCard.enchanted ||
                            ['Enchanted', 'Iconic', 'Epic', 'Special', 'Super Rare', 'Legendary', 'Rare'].includes(hoverCard.rarity || '')
                          }
                          holo={hoverCard.enchanted || ['Enchanted', 'Iconic', 'Epic'].includes(hoverCard.rarity || '')}
                          showGlow={ownedIds.has(hoverCard.id)}
                          className="aspect-[2/3] w-full"
                        />
                      </button>
                      <div className="mt-2 text-center">
                        <p className="font-display text-sm font-semibold text-foreground truncate">{hoverCard.name}</p>
                        {hoverCard.subtitle && (
                          <p className="text-[10px] text-muted-foreground font-body truncate">{hoverCard.subtitle}</p>
                        )}
                        <div className="mt-1 flex items-center justify-center gap-1.5 flex-wrap">
                          <span className="text-[9px] uppercase tracking-wider text-primary/80 font-body">
                            {hoverCard.set_name} · #{hoverCard.set_number}
                          </span>
                          {hoverCard.rarity && (
                            <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[9px] uppercase tracking-wider text-primary font-body">
                              {hoverCard.rarity}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setDetailCard(hoverCard)}
                          className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[10px] font-medium font-body text-primary-foreground"
                        >
                          View details
                        </button>
                      </div>
                      <button
                        onClick={() => setHoverCard(null)}
                        aria-label="Close preview"
                        className="absolute -top-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full bg-card border border-border text-foreground"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Page indicator */}
              <div className="flex items-center justify-center gap-2">
                <span className="rounded-full border border-border bg-card px-3 py-1 text-[11px] font-body text-foreground">
                  Page <span className="font-display font-semibold text-primary">{safePage + 1}</span>
                  <span className="text-muted-foreground"> of {totalPages}</span>
                </span>
              </div>
            </>

          )}
        </>
      )}

      {/* Editor sheet */}
      {editorOpen && editingBinder && (
        <BinderEditor
          binder={editingBinder}
          allCards={allCards || []}
          sets={sets}
          onSave={saveEditor}
          onClose={() => setEditorOpen(false)}
        />
      )}

      {/* Full card detail sheet */}
      {detailCard && (
        <CardDetailSheet
          card={detailCard}
          owned={ownedIds.has(detailCard.id)}
          ownedQty={collection?.filter((c) => c.card_id === detailCard.id).length || 0}
          wishlisted={false}
          isSignedIn={!!user}
          onClose={() => setDetailCard(null)}
          onAddToCollection={() => { /* handled elsewhere */ }}
          onToggleWishlist={() => { /* handled elsewhere */ }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Editor                                                             */
/* ------------------------------------------------------------------ */

interface EditorProps {
  binder: Binder;
  allCards: Card[];
  sets: string[];
  onSave: (b: Binder) => void;
  onClose: () => void;
}

function BinderEditor({ binder, allCards, sets, onSave, onClose }: EditorProps) {
  const [draft, setDraft] = useState<Binder>(binder);
  const [cardSearch, setCardSearch] = useState('');

  const update = <K extends keyof Binder>(key: K, val: Binder[K]) =>
    setDraft((d) => ({ ...d, [key]: val }));

  const characters = useMemo(() => {
    // Collect unique first-name characters from card names ("Name - Title")
    const set = new Set<string>();
    for (const c of allCards) {
      const base = c.name.split(' - ')[0].trim();
      if (base) set.add(base);
    }
    return Array.from(set).sort();
  }, [allCards]);

  const filteredCards = useMemo(() => {
    const q = cardSearch.trim().toLowerCase();
    if (!q) return [];
    return allCards
      .filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.subtitle || '').toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [cardSearch, allCards]);

  const chosenSet = new Set(draft.cardIds);
  const addCard = (id: string) => {
    if (chosenSet.has(id)) return;
    update('cardIds', [...draft.cardIds, id]);
  };
  const removeCard = (id: string) =>
    update(
      'cardIds',
      draft.cardIds.filter((x) => x !== id),
    );

  const chosenCards = draft.cardIds
    .map((id) => allCards.find((c) => c.id === id))
    .filter(Boolean) as Card[];

  const showSetPicker =
    draft.type === 'master' || draft.type === 'grand-master';
  const showCharacterPicker = draft.type === 'character';
  const showCardBuilder = draft.type === 'regular' || draft.type === 'custom';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-[430px] max-h-[90vh] overflow-y-auto rounded-t-2xl border border-border bg-card p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-semibold text-foreground">
            Binder Setup
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Name */}
        <div>
          <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">
            Name
          </p>
          <input
            value={draft.name}
            onChange={(e) => update('name', e.target.value)}
            className="w-full rounded-lg border border-border bg-secondary px-2.5 py-2 text-xs text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Type */}
        <div>
          <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">
            Binder Type
          </p>
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(TYPE_LABEL) as BinderType[]).map((t) => (
              <button
                key={t}
                onClick={() => update('type', t)}
                className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${
                  draft.type === t
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground'
                }`}
              >
                {TYPE_LABEL[t]}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[10px] text-muted-foreground font-body">
            {TYPE_HINT[draft.type]}
          </p>
        </div>

        {/* Pocket */}
        <div>
          <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">
            Pocket Layout
          </p>
          <div className="flex gap-1.5">
            {([4, 9, 12] as Pocket[]).map((p) => (
              <button
                key={p}
                onClick={() => update('pocket', p)}
                className={`flex-1 rounded-lg py-2 text-xs font-medium font-body transition-colors ${
                  draft.pocket === p
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground'
                }`}
              >
                {p}-pocket
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[10px] text-muted-foreground font-body">
            {draft.pocket === 9
              ? 'Standard 9-pocket binder — up to 360 cards single-sided.'
              : draft.pocket === 12
                ? '12-pocket binder — up to ~480 cards single-sided.'
                : '4-pocket toploader binder — great for premium cards.'}
          </p>
        </div>

        {/* Set picker */}
        {showSetPicker && (
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">
              Set
            </p>
            <select
              value={draft.setName || ''}
              onChange={(e) => update('setName', e.target.value)}
              className="w-full rounded-lg border border-border bg-secondary px-2.5 py-2 text-xs text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">Choose a set…</option>
              {sets.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Character picker */}
        {showCharacterPicker && (
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">
              Character
            </p>
            <input
              list="binder-characters"
              value={draft.character || ''}
              onChange={(e) => update('character', e.target.value)}
              placeholder="e.g. Mickey Mouse, Elsa"
              className="w-full rounded-lg border border-border bg-secondary px-2.5 py-2 text-xs text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <datalist id="binder-characters">
              {characters.slice(0, 200).map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
        )}

        {/* Card builder */}
        {showCardBuilder && (
          <div className="space-y-2">
            <p className="text-[10px] text-muted-foreground font-body uppercase tracking-wider">
              Cards ({draft.cardIds.length})
            </p>

            {draft.type === 'custom' && (
              <CustomBinderScout
                binderName={draft.name}
                allCards={allCards}
                sets={sets}
                chosenIds={draft.cardIds}
                onAccept={(id) => addCard(id)}
                onAcceptMany={(ids) =>
                  update('cardIds', Array.from(new Set([...draft.cardIds, ...ids])))
                }
              />
            )}



            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={cardSearch}
                onChange={(e) => setCardSearch(e.target.value)}
                placeholder="Search card to add…"
                className="w-full rounded-lg border border-border bg-secondary py-2 pl-9 pr-3 text-xs text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {filteredCards.length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded-lg border border-border bg-secondary/40">
                {filteredCards.map((c) => {
                  const already = chosenSet.has(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => addCard(c.id)}
                      disabled={already}
                      className="flex w-full items-center gap-2 border-b border-border/50 px-2 py-1.5 text-left last:border-0 disabled:opacity-40"
                    >
                      <span className="flex-1 truncate text-[11px] text-foreground font-body">
                        {c.name}
                        {c.subtitle ? (
                          <span className="text-muted-foreground">
                            {' '}
                            — {c.subtitle}
                          </span>
                        ) : null}
                      </span>
                      {already ? (
                        <Check className="h-3.5 w-3.5 text-primary" />
                      ) : (
                        <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {chosenCards.length > 0 && (
              <div className="rounded-lg border border-border bg-secondary/40 p-2">
                <div className="flex flex-wrap gap-1">
                  {chosenCards.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => removeCard(c.id)}
                      className="flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] text-foreground font-body"
                    >
                      {c.name}
                      <X className="h-2.5 w-2.5" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-border bg-secondary py-2 text-xs font-medium font-body text-secondary-foreground"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(draft)}
            disabled={!draft.name.trim()}
            className="flex-1 rounded-lg bg-primary py-2 text-xs font-medium font-body text-primary-foreground disabled:opacity-40"
          >
            Save Binder
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Custom binder — Scout preferences form + suggestions                */
/* ------------------------------------------------------------------ */

interface Suggestion {
  id: string;
  name: string;
  subtitle: string | null;
  set_name: string | null;
  ink_color: string | null;
  rarity: string | null;
  image_url: string | null;
  quantity: number;
  reason: string;
  price_usd: number | null;
  price_updated: string | null;
}

interface PrevSuggestion {
  id: string;
  name: string;
  subtitle: string | null;
  image_url: string | null;
  price_usd: number | null;
}

interface ScoutProps {
  binderName: string;
  allCards: Card[];
  sets: string[];
  chosenIds: string[];
  onAccept: (id: string) => void;
  onAcceptMany: (ids: string[]) => void;
}

const INKS_LIST: InkColor[] = ['Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'];

function CustomBinderScout({ binderName, sets, chosenIds, onAccept, onAcceptMany }: ScoutProps) {
  const [format, setFormat] = useState<'core' | 'archive' | 'any'>('core');
  const [budget, setBudget] = useState<string>('');
  const [inks, setInks] = useState<InkColor[]>([]);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [targetSize, setTargetSize] = useState<number>(24);
  const [notes, setNotes] = useState('');
  const [showForm, setShowForm] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [previous, setPrevious] = useState<PrevSuggestion[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const toggleInk = (i: InkColor) =>
    setInks((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));
  const toggleExcluded = (s: string) =>
    setExcluded((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      // Keep the current suggestions in "previous" so the user can compare across regenerations.
      const prevIds = suggestions.map((s) => s.id);
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error: fnErr } = await supabase.functions.invoke('design-binder', {
        body: {
          name: binderName,
          format,
          budgetUsd: budget ? Number(budget) : null,
          inks,
          excludedSets: excluded,
          targetSize,
          notes: notes.trim() || undefined,
          previousCardIds: prevIds,
        },
      });
      if (fnErr) throw fnErr;
      if ((data as any)?.error) throw new Error((data as any).error);
      const sugg: Suggestion[] = (data as any)?.suggestions ?? [];
      const prev: PrevSuggestion[] = (data as any)?.previous ?? [];
      setSuggestions(sugg);
      setPrevious(prev);
      setDismissed(new Set());
      setShowForm(false);
    } catch (e: any) {
      setError(e?.message || 'Scout could not design the binder right now.');
    } finally {
      setLoading(false);
    }
  };

  const visible = suggestions.filter((s) => !dismissed.has(s.id));
  const totalUsd = visible.reduce((sum, s) => sum + (s.price_usd ?? 0) * s.quantity, 0);
  const acceptAll = () => {
    const ids = visible.filter((s) => !chosenIds.includes(s.id)).map((s) => s.id);
    if (ids.length) onAcceptMany(ids);
  };

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span className="text-[11px] font-medium font-body text-primary">Scout binder designer</span>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="text-[10px] text-muted-foreground font-body underline underline-offset-2"
        >
          {showForm ? 'Hide preferences' : 'Edit preferences'}
        </button>
      </div>

      {showForm && (
        <div className="space-y-2.5">
          {/* Format */}
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1 uppercase tracking-wider">Format</p>
            <div className="flex gap-1.5">
              {(['core', 'archive', 'any'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body ${
                    format === f ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
                  }`}
                >
                  {f === 'any' ? 'Any' : f === 'core' ? 'Core' : 'Archive'}
                </button>
              ))}
            </div>
          </div>

          {/* Budget + size */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] text-muted-foreground font-body mb-1 uppercase tracking-wider">Budget (USD)</p>
              <input
                inputMode="decimal"
                value={budget}
                onChange={(e) => setBudget(e.target.value.replace(/[^\d.]/g, ''))}
                placeholder="e.g. 100"
                className="w-full rounded-lg border border-border bg-secondary px-2.5 py-1.5 text-xs text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground font-body mb-1 uppercase tracking-wider"># of cards</p>
              <input
                type="number"
                min={6}
                max={60}
                value={targetSize}
                onChange={(e) => setTargetSize(Math.min(Math.max(parseInt(e.target.value || '0', 10) || 0, 6), 60))}
                className="w-full rounded-lg border border-border bg-secondary px-2.5 py-1.5 text-xs text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {/* Inks */}
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1 uppercase tracking-wider">Target ink(s)</p>
            <div className="flex flex-wrap gap-1.5">
              {INKS_LIST.map((i) => (
                <button
                  key={i}
                  onClick={() => toggleInk(i)}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body ${
                    inks.includes(i) ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
                  }`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>

          {/* Excluded sets */}
          {sets.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground font-body mb-1 uppercase tracking-wider">Exclude sets</p>
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                {sets.map((s) => (
                  <button
                    key={s}
                    onClick={() => toggleExcluded(s)}
                    className={`rounded-full px-2 py-0.5 text-[10px] font-body ${
                      excluded.includes(s)
                        ? 'bg-destructive/20 text-destructive line-through'
                        : 'bg-secondary text-secondary-foreground'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1 uppercase tracking-wider">Notes for Scout (optional)</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. lean into Mickey Mouse cards, avoid Enchanteds…"
              className="w-full rounded-lg border border-border bg-secondary px-2.5 py-1.5 text-xs text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <button
            onClick={generate}
            disabled={loading}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary py-2 text-[11px] font-medium font-body text-primary-foreground disabled:opacity-50"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {loading ? 'Designing…' : suggestions.length ? 'Regenerate with these prefs' : 'Design my binder'}
          </button>
        </div>
      )}

      {error && (
        <p className="rounded-lg bg-destructive/10 px-2 py-1.5 text-[10px] font-body text-destructive">{error}</p>
      )}

      {/* Suggestions */}
      {visible.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-muted-foreground font-body uppercase tracking-wider">
              Scout's picks · {visible.length}
            </p>
            <p className="text-[10px] font-medium font-body text-foreground">
              Est. ${totalUsd.toFixed(2)}
            </p>
          </div>

          <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
            {visible.map((s) => {
              const already = chosenIds.includes(s.id);
              return (
                <div
                  key={s.id}
                  className="flex items-start gap-2 rounded-lg border border-border/70 bg-card p-2"
                >
                  {s.image_url ? (
                    <img
                      src={s.image_url}
                      alt=""
                      loading="lazy"
                      className="h-12 w-9 flex-shrink-0 rounded object-cover"
                    />
                  ) : (
                    <div className="h-12 w-9 flex-shrink-0 rounded bg-secondary" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-[11px] font-medium font-body text-foreground">
                      {s.name}
                      {s.subtitle ? <span className="text-muted-foreground"> — {s.subtitle}</span> : null}
                    </p>
                    <p className="text-[9px] text-muted-foreground font-body">
                      {s.set_name || ''} {s.rarity ? `· ${s.rarity}` : ''} · qty {s.quantity}
                      {s.price_usd != null ? ` · $${s.price_usd.toFixed(2)}` : ' · no recent sale'}
                    </p>
                    {s.reason && (
                      <p className="mt-0.5 text-[10px] text-muted-foreground font-body line-clamp-2">
                        {s.reason}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-shrink-0 flex-col gap-1">
                    <button
                      onClick={() => onAccept(s.id)}
                      disabled={already}
                      className="rounded-md bg-primary px-2 py-0.5 text-[10px] font-medium font-body text-primary-foreground disabled:opacity-40"
                    >
                      {already ? 'Added' : 'Accept'}
                    </button>
                    <button
                      onClick={() =>
                        setDismissed((d) => {
                          const next = new Set(d);
                          next.add(s.id);
                          return next;
                        })
                      }
                      className="rounded-md border border-border px-2 py-0.5 text-[10px] font-body text-muted-foreground"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={acceptAll}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 py-1.5 text-[10px] font-medium font-body text-primary"
          >
            <Plus className="h-3 w-3" /> Accept all remaining
          </button>
        </div>
      )}

      {/* Previous suggestions kept for quick comparison */}
      {previous.length > 0 && (
        <details className="rounded-lg border border-border bg-secondary/30 p-2">
          <summary className="cursor-pointer text-[10px] font-body text-muted-foreground">
            Previous picks ({previous.length}) — kept for comparison
          </summary>
          <div className="mt-2 flex flex-wrap gap-1">
            {previous.map((p) => (
              <span
                key={p.id}
                className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-body text-foreground"
                title={p.price_usd != null ? `$${p.price_usd.toFixed(2)}` : 'no recent sale'}
              >
                {p.name}
                {p.price_usd != null ? ` · $${p.price_usd.toFixed(2)}` : ''}
              </span>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

